import { useEffect, useMemo, useState } from 'react';
import {
  Banknote,
  Plus,
  Wallet,
  Building2,
  Smartphone,
  Users,
  PiggyBank,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { FormField, Input, Select } from '../../components/ui/Form';
import { StatCard } from '../../components/ui/StatCard';
import { useTreasury, type CreateTreasuryAccountPayload } from '../../hooks/useTreasury';
import { useCurrency } from '../../hooks/useCurrency';
import type { TreasuryAccount, TreasuryAccountType } from '../../types/treasury';
import type { CurrencyCode } from '../../config/currency';
import { CURRENCIES } from '../../config/currency';
import toast from 'react-hot-toast';

const TYPE_LABELS: Record<TreasuryAccountType, string> = {
  bank: 'Banque',
  microfinance: 'Microfinance',
  cooperative: 'Coopérative (COOPEC)',
  vsla: 'VSLA',
  mobile_money: 'Mobile Money',
};

const TYPE_ICONS: Record<TreasuryAccountType, typeof Banknote> = {
  bank: Building2,
  microfinance: PiggyBank,
  cooperative: Users,
  vsla: Wallet,
  mobile_money: Smartphone,
};

const TYPE_BADGE: Record<TreasuryAccountType, { bg: string; text: string }> = {
  bank: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
  microfinance: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400' },
  cooperative: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
  vsla: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' },
  mobile_money: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400' },
};

export function TreasuryPage() {
  const { accounts, loading, saving, error, loadAccounts, createAccount } = useTreasury();
  const { formatConverted } = useCurrency();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  const totalBalance = useMemo(
    () => accounts.reduce((sum, a) => sum + Number(a.balance ?? 0), 0),
    [accounts],
  );
  const accountsByType = useMemo(() => {
    const map: Partial<Record<TreasuryAccountType, TreasuryAccount[]>> = {};
    for (const acc of accounts) {
      const type = acc.type;
      if (!map[type]) map[type] = [];
      map[type]!.push(acc);
    }
    return map;
  }, [accounts]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Trésorerie</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Comptes bancaires, microfinance, coopératives, VSLA et mobile money
          </p>
        </div>
        <Button variant="success" icon={Plus} onClick={() => setShowModal(true)}>
          Nouveau compte
        </Button>
      </div>

      {error && (
        <div className="p-3 rounded border border-destructive/30 bg-destructive/5 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard title="Comptes ouverts" value={accounts.filter((a) => a.isActive !== false).length} icon={Banknote} />
        <StatCard title="Solde consolidé" value={formatConverted(totalBalance)} icon={Wallet} />
        <StatCard title="Types couverts" value={Object.keys(accountsByType).length} icon={Building2} />
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-muted border-t-primary" />
        </div>
      ) : accounts.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Wallet className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">
              Aucun compte de trésorerie configuré pour cette entreprise.
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accounts.map((account) => {
            const Icon = TYPE_ICONS[account.type];
            const badge = TYPE_BADGE[account.type];
            return (
              <Card key={account.id}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${badge.bg}`}>
                      <Icon className={`h-5 w-5 ${badge.text}`} />
                    </div>
                    <div>
                      <p className="font-semibold">
                        {account.name ?? account.bankName ?? 'Compte'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {account.accountNumber} · {account.currency}
                      </p>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium mt-2 ${badge.bg} ${badge.text}`}
                      >
                        {TYPE_LABELS[account.type]}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Solde</p>
                    <p className="font-semibold text-lg">
                      {formatConverted(Number(account.balance ?? 0))}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <CreateTreasuryAccountModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onCreate={async (payload) => {
          const created = await createAccount(payload);
          if (created) {
            toast.success('Compte de trésorerie créé');
            setShowModal(false);
          } else {
            toast.error("Échec de la création");
          }
        }}
        saving={saving}
      />
    </div>
  );
}

interface CreateTreasuryAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (payload: CreateTreasuryAccountPayload) => Promise<void>;
  saving: boolean;
}

function CreateTreasuryAccountModal({
  isOpen,
  onClose,
  onCreate,
  saving,
}: CreateTreasuryAccountModalProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<TreasuryAccountType>('bank');
  const [accountNumber, setAccountNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('CDF');

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setType('bank');
    setAccountNumber('');
    setBankName('');
    setCurrency('CDF');
  }, [isOpen]);

  const currencyOptions = Object.keys(CURRENCIES).map((code) => ({
    value: code,
    label: code,
  }));

  const typeOptions = (Object.keys(TYPE_LABELS) as TreasuryAccountType[]).map((t) => ({
    value: t,
    label: TYPE_LABELS[t],
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onCreate({
      name,
      type,
      accountNumber,
      bankName: bankName || undefined,
      currency,
      isActive: true,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nouveau compte de trésorerie" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField label="Type de compte" required>
          <Select
            value={type}
            onChange={(e) => setType(e.target.value as TreasuryAccountType)}
            options={typeOptions}
          />
        </FormField>

        <FormField label="Nom du compte" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              type === 'mobile_money' ? 'Ex: Orange Money - Caisse principale' : 'Ex: Compte courant Rawbank'
            }
            required
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Numéro de compte" required>
            <Input
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Devise" required>
            <Select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as CurrencyCode)}
              options={currencyOptions}
            />
          </FormField>
        </div>

        {(type === 'bank' || type === 'microfinance' || type === 'cooperative') && (
          <FormField label="Banque / institution">
            <Input
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder={type === 'bank' ? 'Ex: Rawbank, Equity, Trust Merchant Bank' : 'Nom de l\'institution'}
            />
          </FormField>
        )}

        {type === 'mobile_money' && (
          <FormField label="Opérateur" hint="Orange Money, M-Pesa, Airtel Money, Afrimoney…">
            <Input
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="Ex: Orange Money"
            />
          </FormField>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" isLoading={saving}>
            Créer le compte
          </Button>
        </div>
      </form>
    </Modal>
  );
}
